import {
  DEBUG_OVERRIDE,
  EdgeDefinitionType,
  EdgeTypeMatrixType,
  IBusinessObjectAssembler,
  ModelCommon,
  NOT_LAMBDA,
  PathDirection,
  getParentEdgeTypeId,
} from '@iotv/datamodel-core'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

type IsValidEdgeTypeFnFactoryInput = {
  businessObjectAssembler: IBusinessObjectAssembler
  edgeTypeMatrix: EdgeTypeMatrixType
}

export type IsValidEdgeTypeInput = {
  sourceObjectTypeId: string
  targetObjectTypeId: string
  edgeTypeId?: string
}

export enum InvalidatedReason {
  BAD_EDGE_TYPE_ID = 'BAD_EDGE_TYPE_ID',
  INVALID_TYPES = 'INVALID_TYPES',
  MISSING_CLASS_USER_ATT = 'MISSING_CLASS_USER_ATT',
  MISSING_EDGE_TYPE_ID = 'MISSING_EDGE_TYPE_ID',
}

export type IsValidEdgeTypeOuput = { isValid: boolean; reason?: InvalidatedReason; violation?: string; resolvedEdgeTypeId?: string }

type IsValidEdgeTypeFnFactoryOutput = ({ sourceObjectTypeId, targetObjectTypeId, edgeTypeId }: IsValidEdgeTypeInput) => IsValidEdgeTypeOuput

/** returns a function based on provided edgeTypeMatrix and businessObjectAssember which when called with source, target and edgeTypeIds returns whether combination is valid for this datamodel configuration */
export const isValidEdgeTypeFnFactory = ({ edgeTypeMatrix, businessObjectAssembler }: IsValidEdgeTypeFnFactoryInput): IsValidEdgeTypeFnFactoryOutput => {
  return ({ sourceObjectTypeId, targetObjectTypeId: targetObjectTypeId, edgeTypeId }: IsValidEdgeTypeInput) => {
    const getResolvedEdgeTypeId = (itemType: string, parentType: string): string | undefined => {
      const overrides: Record<string, Record<string, string | undefined>> = {
        Customer: {
          Customer: edgeTypeMatrix.Customer?.Customer?.customer_has_subCustomer_typeZ?.edgeTypeId,
          Device: edgeTypeMatrix.Customer?.Device?.customer_has_been_allocated_device.edgeTypeId,
        },
      }

      return overrides[parentType]?.[itemType] ?? getParentEdgeTypeId(targetObjectTypeId, sourceObjectTypeId)
    }

    const untypeEdgeTypeMatrix: Record<string, Record<string, Record<string, EdgeDefinitionType>>> = edgeTypeMatrix
    const res: IsValidEdgeTypeOuput = { isValid: false, reason: undefined }

    const edgeTypeDefintionMap = untypeEdgeTypeMatrix[sourceObjectTypeId]?.[targetObjectTypeId]
    if (edgeTypeDefintionMap) {
      if (edgeTypeId) {
        /** this absolutely requires the contraint that each entry key must equal the edgeTypeId attribute of the entry */
        res.isValid = !!edgeTypeDefintionMap[edgeTypeId]
        if (!res.isValid) {
          res.reason = InvalidatedReason.BAD_EDGE_TYPE_ID
          res.resolvedEdgeTypeId = getResolvedEdgeTypeId(targetObjectTypeId, sourceObjectTypeId)
          res.violation = edgeTypeId
        }
      } else {
        res.reason = InvalidatedReason.MISSING_EDGE_TYPE_ID
        res.isValid = false
        res.resolvedEdgeTypeId = getResolvedEdgeTypeId(targetObjectTypeId, sourceObjectTypeId)
        res.violation = edgeTypeId
      }
      if (!res.isValid) {
        debug &&
          console.log(
            `${res.reason} between ${sourceObjectTypeId} and ${targetObjectTypeId} from edgeTypeMatix: ${res.violation}, ${res.resolvedEdgeTypeId ? `proposed: ${res.resolvedEdgeTypeId}` : ''}`,
          )
      }
    } else {
      /** userAttribute.name is very legacy and needs to be removed from all class defined links */
      /** get legacy definition from source and target classes */
      try {
        const sourceInstance = businessObjectAssembler.getInstanceFromPrimaryKey(`${sourceObjectTypeId}:`) as ModelCommon
        const userAttributes = sourceInstance?.getUserAttributes()

        const parentDefinedUserAttDefs = Object.values(userAttributes).find(
          (userAttributeDef) =>
            (userAttributeDef?.objectTypeId ?? userAttributeDef?.name) === targetObjectTypeId && userAttributeDef?.direction === PathDirection.child,
        )
        const childDefinedUserAttDefs = Object.values(userAttributes).find(
          (userAttributeDef) =>
            (userAttributeDef?.objectTypeId ?? userAttributeDef?.name) === sourceObjectTypeId && userAttributeDef?.direction === PathDirection.parent,
        )

        if (parentDefinedUserAttDefs || childDefinedUserAttDefs) {
          res.isValid = true
        } else {
          res.isValid = false
          res.reason = InvalidatedReason.INVALID_TYPES
        }
      } catch (e: any) {
        // expect errors where class requires certain constructor props

        // to reach here it is likely the edgeTypeMatrix is missing the defintion!
        const { message } = e
        ;(res.isValid = false), (res.reason = message)
      }
    }
    return res
  }
}
